import { Trans } from '@lingui/react/macro'
import {
  MUSIC_DEFAULT_CREDITS,
  TTS_CREDITS_PER_1000_CHARS,
} from '@loc/server/constant/common'

import type { ReactNode } from 'react'
import type { TProduct } from '@/contexts/PlanContext/type'
import type { TListItem } from '@/contexts/PlanContext/hooks/productText'

import { EPaidProductType, EProductType } from '@/contexts/PlanContext/type'
import { getCurrentPrice } from '@/contexts/PlanContext/helpers/getCurrentPrice'
import { getProductType } from '@/contexts/PlanContext/helpers/getProductType'
import { ProductsSubUsageType } from '@/contexts/SiteSpecificContext/types'

export const WEEKLY_DURATION = 7

export type TProductBillingPeriod = 'week' | 'month' | 'year'

export function getCreditsUsage(product: TProduct) {
  return product.usageList.find(
    (usage) => usage.usageType === ProductsSubUsageType.credits,
  )
}

export function getProductCreditsCount(product: TProduct) {
  return Number(getCreditsUsage(product)?.usageCount || 0)
}

export function isWeeklyCreditsProduct(
  product?: Pick<TProduct, 'duration' | 'usageList'>,
) {
  if (!product) {
    return false
  }

  const creditsUsage = product.usageList.find(
    (usage) => usage.usageType === ProductsSubUsageType.credits,
  )

  return creditsUsage?.period === 'week'
}

export function getProductBillingPeriod(
  product?: Pick<TProduct, 'duration' | 'usageList'>,
): TProductBillingPeriod {
  if (!product) {
    return 'month'
  }

  if (isWeeklyCreditsProduct(product)) {
    return 'week'
  }

  if (product.duration === 12) {
    return 'year'
  }

  return 'month'
}

export function isPlusProduct(product: Pick<TProduct, 'productName'>) {
  return product.productName.toLowerCase().includes('plus')
}

export function isStandardProduct(product: Pick<TProduct, 'productName'>) {
  return product.productName.toLowerCase().includes('standard')
}

export function isProProduct(product: Pick<TProduct, 'productName'>) {
  return product.productName.toLowerCase().includes('pro')
}

export function getConcurrentMusicGenerationLabel(product: TProduct) {
  const limitCount = getCreditsUsage(product)?.limitRequestCount || 0
  const limitCountText = limitCount.toLocaleString()

  return (
    <Trans>
      Up to <span className='text-f-text'>{limitCountText}</span> concurrent
      music generations
    </Trans>
  )
}

export function getPlanWordsText(product: TProduct) {
  const productType = getProductType({ product })
  const creditsCount = getProductCreditsCount(product)
  const songCount = Math.floor(creditsCount / MUSIC_DEFAULT_CREDITS)
  const songUnitPriceText = getProductSongUnitPriceText(product)

  if (productType === EProductType.FREE) {
    return (
      <div className='mb-6'>
        <div className='text-f-text flex items-center justify-start gap-x-[2px] text-xs'>
          <span className='text-base font-semibold'>
            {creditsCount.toLocaleString()}
          </span>
          <span>
            <Trans>Credits({songCount} Songs)</Trans>
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className='mb-2'>
      <div className='text-f-text flex items-center justify-start gap-x-[2px] text-xs'>
        <span className='text-base font-semibold'>
          {creditsCount.toLocaleString()}
        </span>
        <span>
          <Trans>Credits / Month</Trans>
        </span>
      </div>
      <div className='text-f-text-tertiary text-xs'>
        <Trans>{songUnitPriceText} per song</Trans>
      </div>
    </div>
  )
}

export function getProductSongsCount(product: TProduct) {
  return Math.floor(getProductCreditsCount(product) / MUSIC_DEFAULT_CREDITS)
}

export function getProductSongUnitPriceText(product: TProduct) {
  const songCount = getProductSongsCount(product)

  if (!songCount) {
    return '0'
  }

  const { currencyPriceMonth, realCurrencyInfo } = getCurrentPrice({
    productPrice: product,
  })
  const symbol = realCurrencyInfo?.symbol || '$'

  return `${symbol}${(currencyPriceMonth / songCount).toFixed(2)}`
}

export function getPlanFeatureItems(product: TProduct) {
  const productType = getProductType({ product })
  const isFree = productType === EProductType.FREE
  const isPlus = isPlusProduct(product)
  const isStandard = isStandardProduct(product)
  // 默认套餐页这里混用了“聚合类型”和“SKU 名称关键词”：
  // BASIC 下面还会继续拆 plus / standard，不同分支展示的权益并不完全一样。
  const isProLike =
    isProProduct(product) || productType === EPaidProductType.UNLIMITED

  if (isPlus) {
    return [
      {
        label: <Trans>Standard features only</Trans>,
        enabled: true,
      },
      {
        label: <Trans>Commercial use</Trans>,
        enabled: false,
      },
      {
        label: <Trans>Upload reference audio files</Trans>,
        enabled: false,
      },
    ] satisfies TListItem[]
  }

  if (isFree) {
    return [
      {
        label: <Trans>Standard features only</Trans>,
        enabled: true,
      },
      {
        label: <Trans>Commercial use</Trans>,
        enabled: false,
      },
      {
        label: <Trans>Upload reference audio files</Trans>,
        enabled: false,
      },
    ] satisfies TListItem[]
  }

  if (isStandard) {
    return [
      {
        label: getConcurrentMusicGenerationLabel(product),
        enabled: true,
      },
      {
        label: <Trans>AI music generation</Trans>,
        enabled: true,
      },
      {
        label: <Trans>AI cover generation</Trans>,
        enabled: true,
      },
      {
        label: <Trans>Text to speech</Trans>,
        enabled: true,
      },
      {
        label: <Trans>AI-powered lyrics enhancement</Trans>,
        enabled: true,
      },
      {
        label: <Trans>Rich lyrics editing features</Trans>,
        enabled: true,
      },
      {
        label: <Trans>Pro-level style prompts by AI</Trans>,
        enabled: true,
      },
      {
        label: <Trans>Commercial use</Trans>,
        enabled: true,
      },
      {
        label: <Trans>Upload reference audio files</Trans>,
        enabled: false,
      },
    ] satisfies TListItem[]
  }

  return [
    {
      label: getConcurrentMusicGenerationLabel(product),
      enabled: true,
    },
    {
      label: <Trans>AI music generation</Trans>,
      enabled: true,
    },
    {
      label: <Trans>AI cover generation</Trans>,
      enabled: true,
    },
    {
      label: <Trans>Text to speech</Trans>,
      enabled: true,
    },
    {
      label: <Trans>AI-powered lyrics enhancement</Trans>,
      enabled: true,
    },
    {
      label: <Trans>Rich lyrics editing features</Trans>,
      enabled: true,
    },
    {
      label: <Trans>Pro-level style prompts by AI</Trans>,
      enabled: true,
    },
    {
      label: <Trans>Commercial use</Trans>,
      enabled: true,
    },
    {
      label: <Trans>Upload reference audio files</Trans>,
      enabled: isProLike,
    },
  ] satisfies TListItem[]
}

export function formatCompactCount(value: number) {
  if (value >= 1000) {
    const text = (value / 1000).toFixed(1)
    return text.endsWith('.0') ? `${text.slice(0, -2)}K` : `${text}K`
  }

  return `${value}`
}

export interface IExperimentPlanConfig {
  key: 'basic' | 'standard' | 'pro'
  title: ReactNode
  badge?: ReactNode
}

export type TExperimentPlanKey = IExperimentPlanConfig['key']

export interface IExperimentFeatureRow {
  key: string
  label: ReactNode
  type: 'check' | 'dot'
}

export interface IExperimentFeatureSection {
  key: string
  title: ReactNode
  enabled?: boolean
  rows?: IExperimentFeatureRow[]
}

export const experimentPlanConfigs: IExperimentPlanConfig[] = [
  {
    key: 'basic',
    title: <Trans>Basic</Trans>,
  },
  {
    key: 'pro',
    title: <Trans>Pro</Trans>,
    badge: <Trans>Billed yearly</Trans>,
  },
  {
    key: 'standard',
    title: <Trans>Standard</Trans>,
  },
]

export function getExperimentColumnKey(
  product?: Pick<TProduct, 'productName' | 'duration' | 'usageList'>,
): TExperimentPlanKey | undefined {
  if (!product) {
    return undefined
  }

  // A 版三列不是直接按 productType 映射：
  // Basic 看 plus + 周配额，Standard 看 standard + 月付，Pro 看 pro + 年付。
  if (isPlusProduct(product) && isWeeklyCreditsProduct(product)) {
    return 'basic'
  }

  if (
    isStandardProduct(product) &&
    getProductBillingPeriod(product) === 'month'
  ) {
    return 'standard'
  }

  if (isProProduct(product) && getProductBillingPeriod(product) === 'year') {
    return 'pro'
  }

  return undefined
}

export function getTextToSpeechCharacterCount(product: TProduct) {
  return getProductCreditsCount(product) * (1000 / TTS_CREDITS_PER_1000_CHARS)
}

export function getProductCreditsSummaryText(product: TProduct) {
  const credits = getProductCreditsCount(product)
  const billingPeriod = getProductBillingPeriod(product)

  if (billingPeriod === 'week') {
    return (
      <>
        <span className='text-f-text'>{credits}</span>{' '}
        <Trans>credits/week</Trans>
      </>
    )
  }

  return (
    <>
      <span className='text-f-text'>{credits}</span>{' '}
      <Trans>credits/month</Trans>
    </>
  )
}

export function getProductSpeechCharactersText(product: TProduct) {
  return formatCompactCount(getTextToSpeechCharacterCount(product))
}

function getExperimentFeatureSections(
  product: TProduct,
): IExperimentFeatureSection[] {
  const songCount = getProductSongsCount(product)
  const speechCharacters = getProductSpeechCharactersText(product)
  const concurrentSongs = getCreditsUsage(product)?.limitRequestCount || 0
  // 实验版权益直接跟随“落到哪一列”来开关，而不是复用默认页的 productType 规则。
  const columnKey = getExperimentColumnKey(product)
  const canUploadReference = columnKey === 'pro'

  return [
    {
      key: 'credits',
      title: getProductCreditsSummaryText(product),
      rows: [
        {
          key: 'generate',
          type: 'dot',
          label: (
            <Trans>
              Up to <strong>{songCount}</strong> songs
            </Trans>
          ),
        },
        {
          key: 'cover',
          type: 'dot',
          label: (
            <Trans>
              Up to <strong>{songCount}</strong> AI covers
            </Trans>
          ),
        },
        {
          key: 'speech',
          type: 'dot',
          label: (
            <Trans>
              Convert up to <strong>{speechCharacters}</strong> characters to
              speech
            </Trans>
          ),
        },
      ],
    },
    {
      key: 'concurrent',
      title: (
        <Trans>
          Up to <strong className='text-f-text'>{concurrentSongs}</strong>{' '}
          concurrent music generations
        </Trans>
      ),
    },
    {
      key: 'music',
      title: <Trans>AI music generation</Trans>,
    },
    {
      key: 'covers',
      title: <Trans>AI cover generation</Trans>,
    },
    {
      key: 'tts',
      title: <Trans>Text to speech</Trans>,
    },
    {
      key: 'lyrics-enhancement',
      title: <Trans>AI-powered lyrics enhancement</Trans>,
    },
    {
      key: 'lyrics-editing',
      title: <Trans>Rich lyrics editing features</Trans>,
    },
    {
      key: 'prompts',
      title: <Trans>Pro-level style prompts by AI</Trans>,
    },
    {
      key: 'commercial',
      title: <Trans>Commercial use</Trans>,
      // enabled: canCommercialUse,
    },
    {
      key: 'reference-audio',
      title: <Trans>Upload reference audio files</Trans>,
      enabled: canUploadReference,
    },
  ]
}

function getExperimentFeatureRowsContent(rows: IExperimentFeatureRow[]) {
  return (
    <div className='flex min-w-0 flex-1 flex-col gap-1'>
      {rows.map((row) => (
        <div
          key={row.key}
          className='text-f-text-tertiary flex items-start gap-1 text-xs'
        >
          <span className='mt-[6px] size-1 flex-none rounded-full bg-[#6b6881]'></span>
          <span className='[&_strong]:text-f-text min-w-0 flex-1 [&_strong]:font-semibold'>
            {row.label}
          </span>
        </div>
      ))}
    </div>
  )
}

export function getExperimentListItems(
  product: TProduct,
  options?: {
    isModal?: boolean
  },
): TListItem[] {
  const { isModal = false } = options ?? {}

  return getExperimentFeatureSections(product).map((section) => {
    if (!section.rows?.length) {
      return {
        enabled: section.enabled ?? true,
        label: section.title,
      }
    }

    const rowsContent = getExperimentFeatureRowsContent(section.rows)

    if (isModal) {
      return {
        enabled: true,
        label: section.title,
        tooltipContent: rowsContent,
      }
    }

    return {
      enabled: true,
      label: (
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <div>{section.title}</div>
          {rowsContent}
        </div>
      ),
    }
  })
}

export function getProductPriceText(product: TProduct) {
  const priceValue =
    getProductBillingPeriod(product) === 'year'
      ? product.price / 100 / product.duration
      : product.price / 100

  return priceValue.toFixed(2)
}

export function getProductPriceSuffix(product: TProduct) {
  const billingPeriod = getProductBillingPeriod(product)

  if (billingPeriod === 'week') {
    return <Trans>/week</Trans>
  }

  return <Trans>/month</Trans>
}
